import { useMemo, useState } from 'react'
import { FileDown, FileOutput, FileText, Image, Presentation } from 'lucide-react'
import type { OutputApproval } from '@mindkeep/shared'
import { useVaultFiles } from '@/features/vault/use-vault'
import { getSignedUrl } from '@/features/output/output-api'
import { useOutputApprovals } from '@/features/output/use-output-approvals'
import {
  OutputApprovalActions,
  OutputApprovalBadge,
  OutputApprovalDecisionInfo,
} from '@/features/output/output-approval'
import { useWorkspaceRole } from '@/features/workspace/use-workspace-role'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/page-header'
import { EmptyState } from '@/components/empty-state'
import { ErrorState } from '@/components/error-state'
import { SkeletonList } from '@/components/skeleton-list'
import { toast } from 'sonner'

function outputIcon(name: string) {
  const lower = name.toLowerCase()
  if (lower.endsWith('.pdf')) return Presentation
  if (lower.endsWith('.html')) return FileText
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return Image
  return FileOutput
}

function formatSize(bytes?: number) {
  if (bytes === undefined) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function OutputsPage() {
  const { data: vault, isLoading, isError, error, refetch, isFetching } = useVaultFiles()
  const [downloading, setDownloading] = useState<Set<string>>(new Set())
  const approvalsQuery = useOutputApprovals()
  const workspaceRole = useWorkspaceRole()

  const outputs = useMemo(
    () =>
      (vault?.entries ?? [])
        .filter((f) => f.type === 'file' && f.path.startsWith('_Outputs/'))
        .sort((a, b) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime()),
    [vault],
  )

  const approvalsByPath = useMemo(() => {
    const map = new Map<string, OutputApproval>()
    for (const approval of approvalsQuery.data?.approvals ?? []) {
      map.set(approval.vault_file_path, approval)
    }
    return map
  }, [approvalsQuery.data])

  async function handleDownload(path: string) {
    if (downloading.has(path)) return
    setDownloading((prev) => new Set(prev).add(path))
    try {
      const url = await getSignedUrl(path)
      window.open(url, '_blank')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download konnte nicht erstellt werden')
    } finally {
      setDownloading((prev) => {
        const next = new Set(prev)
        next.delete(path)
        return next
      })
    }
  }

  return (
    // F06b2: derselbe Arbeitsrahmen wie Start — max 1180px, mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
      <PageHeader
        title="Outputs"
        description="Erzeugte Dokumente, Präsentationen und Posts aus deinem Wissen."
      />

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : isError ? (
        <ErrorState
          title="Outputs konnten nicht geladen werden"
          description={error instanceof Error ? error.message : 'Bitte versuche es erneut.'}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      ) : outputs.length === 0 ? (
        <EmptyState
          icon={FileOutput}
          title="Noch keine Outputs"
          description="Erzeuge deinen ersten Output über Automatisierungen oder im Chat."
        />
      ) : (
        <div className="space-y-3">
          {outputs.map((file) => {
            const Icon = outputIcon(file.name)
            const approval = approvalsByPath.get(file.path)
            return (
              <Card key={file.path}>
                {/* F06b2: ruhige Aktionszeile — flex-wrap + min-w-0, damit lange
                    Dateinamen umbrechen statt Buttons oder Seite zu sprengen. */}
                <CardContent className="flex flex-wrap items-center gap-4 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1 basis-64">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="min-w-0 break-all text-sm font-medium text-foreground">{file.name}</p>
                      {approval && <OutputApprovalBadge status={approval.status} />}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatDate(file.updated_at ?? new Date().toISOString())}
                      {file.size_bytes !== undefined && ` · ${formatSize(file.size_bytes)}`}
                    </p>
                    {approval && (
                      <OutputApprovalDecisionInfo
                        approval={approval}
                        memberEmails={workspaceRole.memberEmails}
                      />
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <OutputApprovalActions
                      path={file.path}
                      approval={approval}
                      canRequestReview={workspaceRole.canRequestReview}
                      canReview={workspaceRole.canReview}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(file.path)}
                      disabled={downloading.has(file.path)}
                    >
                      <FileDown className="mr-2 h-4 w-4" />
                      {downloading.has(file.path) ? 'Wird geladen...' : 'Öffnen'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
